import { api, API_PREFIX, withFallback } from './client'
import type { OnboardingStatus, VerifyEmailResponse } from '../../types/api'

/** GET /onboarding/status (new). Falls back to an empty checklist. */
export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  return withFallback(async () => {
    const res = await api.get<OnboardingStatus>(`${API_PREFIX}/onboarding/status`)
    return res.data ?? { steps: [], progress_pct: 0 }
  }, { steps: [], progress_pct: 0 })
}

/** POST /onboarding/steps/{key}/skip (new) */
export async function skipOnboardingStep(key: string): Promise<void> {
  await api.post(`${API_PREFIX}/onboarding/steps/${encodeURIComponent(key)}/skip`)
}

/** POST /onboarding/steps/{key}/complete (WP-C §6) */
export async function completeOnboardingStep(key: string): Promise<void> {
  await api.post(`${API_PREFIX}/onboarding/steps/${encodeURIComponent(key)}/complete`)
}

/** POST /onboarding/sample-data (new) — load the mock-TMS sample dataset. */
export async function loadSampleData(): Promise<void> {
  await api.post(`${API_PREFIX}/onboarding/sample-data`)
}

/* ── WP-P6: email verification + app QR ────────────────────────────────── */

/** POST /auth/verify-email — public; `{token}` from the emailed link. */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const res = await api.post<VerifyEmailResponse>(`${API_PREFIX}/auth/verify-email`, { token })
  return res.data
}

/** POST /auth/resend-verification (ADMIN). */
export async function resendVerificationEmail(): Promise<{ sent: boolean; already_verified?: boolean }> {
  const res = await api.post(`${API_PREFIX}/auth/resend-verification`)
  return res.data
}

/**
 * GET /onboarding/app-qr.png — the QR is behind auth, so fetch it as a blob
 * and hand back an object URL. Callers must `revokeAppQr()` on unmount.
 */
export async function fetchAppQr(): Promise<string | null> {
  return withFallback(async () => {
    const res = await api.get(`${API_PREFIX}/onboarding/app-qr.png`, { responseType: 'blob' })
    return URL.createObjectURL(res.data as Blob)
  }, null)
}

export function revokeAppQr(url: string | null | undefined): void {
  if (url) {
    try { URL.revokeObjectURL(url) } catch { /* already revoked */ }
  }
}
